import React from 'react';
import styles from '../../styles/CreativeEditor.module.css';

import { SparkIteration, SparkTagDimensionSelection } from '../../types/sparkTypes';
import TagContextSelector, { TagContextSelectorConstraints } from '../tags/TagContextSelector';
import LabelSection from './LabelSection';
import ImageSlot from './ImageSlot';

const EMPTY_TAG_PATTERN: SparkTagDimensionSelection[] = [];

interface HeroSectionProps {
  // Type label (above title)
  typeLabel?: string;

  // Title
  title: string;
  isEditingTitle: boolean;
  editTitle: string;
  isSavingTitle: boolean;
  titleInputRef: React.RefObject<HTMLInputElement>;
  onEditTitleChange: (value: string) => void;
  onStartEditingTitle: () => void;
  onSaveTitle: () => void;
  onCancelEditingTitle: () => void;
  canEditTitle: boolean;
  readOnlyTitleHint?: string;

  // Description
  description: string;
  isEditingDescription: boolean;
  editDescription: string;
  isSavingDescription: boolean;
  descriptionInputRef: React.RefObject<HTMLInputElement>;
  onEditDescriptionChange: (value: string) => void;
  onStartEditingDescription: () => void;
  onSaveDescription: () => void;
  onCancelEditingDescription: () => void;
  canEditDescription: boolean;
  readOnlyDescriptionHint?: string;

  // Hero image
  heroImageUrl?: string | null;
  heroElementId?: string | null;
  heroFileId?: string | null;
  heroIteration?: SparkIteration;
  heroProjectFqn?: string;
  heroEditorMode?: 'iteration' | 'item';
  heroCapabilities?: { canEditMedia: boolean; canUseMediaAI: boolean };
  onHeroIterationUpdated?: (updated: SparkIteration) => void;

  // Tags
  showTags?: boolean;
  tagDimensions?: any;
  tagPattern?: SparkTagDimensionSelection[];
  tagConstraints?: TagContextSelectorConstraints;
  isTagDisabled?: boolean;
  isSavingTags?: boolean;
  tagError?: string | null;
  onPersistTags?: (value: SparkTagDimensionSelection[]) => Promise<void>;
  tagVariant?: 'default' | 'header' | 'compact';
  hideInactiveHeaderChips?: boolean;

  // Publication status
  isPublished?: boolean;

  // Labels (iteration mode)
  showLabels?: boolean;
  labels?: string[];
  availableLabels?: string[];
  isReadOnly?: boolean;
  onAddLabel?: (label: string) => Promise<void>;
  onRemoveLabel?: (label: string) => void;

}

const HeroSection: React.FC<HeroSectionProps> = ({
  typeLabel,
  title,
  isEditingTitle,
  editTitle,
  isSavingTitle,
  titleInputRef,
  onEditTitleChange,
  onStartEditingTitle,
  onSaveTitle,
  onCancelEditingTitle,
  canEditTitle,
  readOnlyTitleHint,

  description,
  isEditingDescription,
  editDescription,
  isSavingDescription,
  descriptionInputRef,
  onEditDescriptionChange,
  onStartEditingDescription,
  onSaveDescription,
  onCancelEditingDescription,
  canEditDescription,
  readOnlyDescriptionHint,

  heroImageUrl,
  heroElementId,
  heroFileId,
  heroIteration,
  heroProjectFqn = '',
  heroEditorMode = 'iteration',
  heroCapabilities,
  onHeroIterationUpdated,

  showTags,
  tagDimensions,
  tagPattern,
  tagConstraints,
  isTagDisabled,
  isSavingTags,
  tagError,
  onPersistTags,
  tagVariant = 'compact',
  hideInactiveHeaderChips,

  isPublished,

  showLabels,
  labels = [],
  availableLabels = [],
  isReadOnly = false,
  onAddLabel,
  onRemoveLabel,

}) => {
  return (
    <div className={styles.viewerHeroSection}>
      {heroImageUrl && (
        <div className={styles.viewerHeroImageContainer}>
          {heroElementId && heroIteration && onHeroIterationUpdated ? (
            <ImageSlot
              elementId={heroElementId}
              elementLabel={title || 'Hero image'}
              fileId={heroFileId ?? null}
              index={0}
              cardinality={1}
              isReadOnly={!heroCapabilities?.canEditMedia}
              iteration={heroIteration}
              projectFqn={heroProjectFqn}
              capabilities={heroCapabilities ?? { canEditMedia: false, canUseMediaAI: false }}
              editorMode={heroEditorMode}
              onIterationUpdated={onHeroIterationUpdated}
              sizeClass={styles.viewerHeroImageSlot}
            />
          ) : (
            <img src={heroImageUrl} alt={title || 'Hero image'} />
          )}
        </div>
      )}
      <div className={styles.viewerHeroText}>
        {typeLabel && (
          <span className={styles.heroTypeLabel}>{typeLabel}</span>
        )}
        {/* Editable title */}
        <div className={styles.creativeTitleRow}>
          {isEditingTitle ? (
            <input
              ref={titleInputRef}
              type="text"
              className={styles.creativeNameInput}
              value={editTitle}
              onChange={(e) => onEditTitleChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSaveTitle();
                if (e.key === 'Escape') onCancelEditingTitle();
              }}
              onBlur={onSaveTitle}
              disabled={isSavingTitle}
              placeholder="Untitled"
            />
          ) : (
            <h1
              className={`${styles.creativeNameDisplay} ${!canEditTitle ? styles.nonInteractive : ''}`}
              onClick={canEditTitle ? onStartEditingTitle : undefined}
              title={canEditTitle ? 'Edit title' : readOnlyTitleHint}
            >
              {title || 'Untitled'}
            </h1>
          )}
        </div>

          {/* Editable description */}
          <div className={styles.creativeDescriptionRow}>
            {isEditingDescription ? (
              <input
                ref={descriptionInputRef}
                type="text"
                className={styles.creativeDescriptionInput}
                value={editDescription}
                onChange={(e) => onEditDescriptionChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSaveDescription();
                  if (e.key === 'Escape') onCancelEditingDescription();
                }}
                onBlur={onSaveDescription}
                disabled={isSavingDescription}
                placeholder="Add a short description"
              />
            ) : (
              <div
                className={`${styles.creativeDescriptionDisplay} ${!canEditDescription ? styles.nonInteractive : ''}`}
                onClick={canEditDescription ? onStartEditingDescription : undefined}
                title={canEditDescription ? 'Edit description' : readOnlyDescriptionHint}
              >
                {description || 'Add a short description'}
              </div>
            )}
          </div>

          {/* Tags */}
          {showTags && tagDimensions && onPersistTags && (
            <div className={styles.heroTagRow}>
              <TagContextSelector
                mode="creative"
                dimensions={tagDimensions}
                value={tagPattern ?? EMPTY_TAG_PATTERN}
                constraints={tagConstraints}
                disabled={isTagDisabled}
                isPersisting={isSavingTags}
                error={tagError}
                onPersist={onPersistTags}
                variant={tagVariant}
                hideInactiveHeaderChips={hideInactiveHeaderChips}
              />
            </div>
          )}

          {/* Status badge + Labels row */}
          {(isPublished || (isPublished === false && heroEditorMode === 'item') || showLabels) && (
            <div className={styles.heroLabelRow}>
              {isPublished !== undefined && (
                isPublished
                  ? <span className={styles.heroPublishedBadge}>Published</span>
                  : heroEditorMode === 'item'
                    ? <span className={styles.heroDraftBadge}>Draft</span>
                    : null
              )}
              {showLabels && onAddLabel && onRemoveLabel && (
                <LabelSection
                  labels={labels}
                  availableLabels={availableLabels}
                  isReadOnly={isReadOnly}
                  onAddLabel={onAddLabel}
                  onRemoveLabel={onRemoveLabel}
                />
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default HeroSection;
